import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

interface Filter {
  category: string
  value: string
}

interface FilterContextType {
  period: string
  setPeriod: (period: string) => void
  filters: Filter[]
  addFilter: (category: string, value: string) => void
  removeFilter: (index: number) => void
  clearAllFilters: () => void
  isFiltering: boolean
  setIsFiltering: (filtering: boolean) => void
}

const FilterContext = createContext<FilterContextType | undefined>(undefined)

interface FilterProviderProps {
  children: ReactNode
}

export function FilterProvider({ children }: FilterProviderProps) {
  const [period, setPeriod] = useState('LAST_7_DAYS') // Default to last 7 days (canonical)
  const [filters, setFilters] = useState<Filter[]>([])
  const [isFiltering, setIsFiltering] = useState(false)

  // Load period from localStorage on mount
  useEffect(() => {
    const savedPeriod = localStorage.getItem('humblytics-date-filter')
    if (savedPeriod) {
      setPeriod(savedPeriod)
    }
  }, [])

  // Save period to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('humblytics-date-filter', period)
  }, [period])

  const addFilter = (category: string, value: string) => {
    setFilters(prev => {
      const exists = prev.find(f => f.category === category)
      if (exists) {
        return prev.map(f => f.category === category ? { category, value } : f)
      }
      return [...prev, { category, value }]
    })
    setIsFiltering(true)
  }

  const removeFilter = (index: number) => {
    setFilters(prev => {
      const updated = prev.filter((_, i) => i !== index)
      setIsFiltering(updated.length > 0)
      return updated
    })
  }

  const clearAllFilters = () => {
    setFilters([])
    setIsFiltering(false)
  }

  const value: FilterContextType = {
    period,
    setPeriod,
    filters,
    addFilter,
    removeFilter,
    clearAllFilters,
    isFiltering,
    setIsFiltering
  }

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  )
}

export function useFilter() {
  const context = useContext(FilterContext)
  if (context === undefined) {
    throw new Error('useFilter must be used within a FilterProvider')
  }
  return context
}
